import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { selectUserTerms } from "Redux/UserSlice";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { deactivateManualForQuestion } from "feature/app/actions";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { TextElement } from "types/types";
import { selectStyle } from "Redux/appSlice";
import {
  selectAppState,
  selectManualActiveByQuestionId,
  setTableOfContent,
} from "../../Redux/appSlice";
import Button from "../Atoms/Button";
import ModuleQuestion from "../Atoms/ModuleQuestion";
import QuestionButton from "../Atoms/QuestionButton";
import RichTextReader from "../Atoms/RichTextReader";
import GuideExample from "../Molecule/GuideExample";
import Term from "../Molecule/Term";
import Terms from "../Molecule/Terms";
import HoverPage from "./HoverPage";

type Props = {
  title: any;
  links: {
    label: string;
    href: string;
    id: string;
  }[];
  id: string;
  answers: {
    label: string;
    id: string;
    single?: boolean;
    guide?: any[];
    example?: any[];
  }[];
  onChange: (id: any, value?: boolean) => void;
  type: "single" | "multiple";
  selected: string[];
  toReset: any;
  conclusion: boolean;
  setCanGoDown: (value: boolean) => void;
  print: boolean;
  iterated: boolean;
  userManualTermId?: string | null;
};

export const MANUAL_TERM_ID = "26017aba-600d-4456-979b-c0bf4fb69e2b";

//  @function QuestionModule
//  Renders an entire question module given a title, links, answers and if it accepts either single or multiple choices
//  @param {string} title - Title (or question itself)
//  @param {{label: string, href: string, id: string}[]} links - List of links to display
//  @param {string} links.label - Link label
//  @param {string} links.href - Redirect for link
//  @param {string} links.id - ID used to recognize a particular link
//  @param {string} id - ID used by the parser to differentiate between questions
//  @param {{label: string, id:string}[]} answers - List of possible answers to display
//  @param {string} answers.label - Answer label
//  @param {string} answers.id - Answer ID
//  @param {"single"|"multiple"} type - Question type if single or multiple answers are accepted
//  @param {function} onChange - called when answers are selected
//  @param {function} setCanGoDown - set if module can go down
//  @returns {React.Node}
export default function QuestionModule({
  title = { title: [[]] },
  id = "",
  selected = [],
  answers = [],
  type = "single",
  onChange = function () {},
  toReset = { value: false, toChange: () => {} },
  conclusion = false,
  setCanGoDown = function () {},
  print = false,
  iterated,
  userManualTermId
}: Props) {
  const [states, changeStates] = useState(
    generateSelectedAnswerMap(answers, selected)
  );
  const [hovered, setHovered] = useState(false);
  const { color2 } = useAppSelector(selectStyle);
  const [outsideAnswers, setOutsideAnswers] = useState(selected);
  const dispatch = useAppDispatch();
  const [sideTextVisibility, setSideTextVisibitlity] = useState(false);
  const [sideTextStatus, setSideTextStatus] = useState(
    generateSideTextFromAnswers(answers, title)
  );
  const [guide, setGuide] = useState<TextElement[]>([["p", {}, "example"]]);
  const [example, setExample] = useState<TextElement[]>([["p", {}, "example"]]);
  const guides = useRef<any | null>(null);
  const state = useAppSelector(selectAppState);
  const manualActive = useAppSelector((state) =>
    selectManualActiveByQuestionId(state, id)
  );
  const userTerms = useAppSelector(selectUserTerms);
  const [terms, setTerms] = useState<
    { id: string; text: TextElement; title: string }[]
  >([]);
  const [multipleIsDifferent, setMultipleIsDifferent] = useState(false);
  const termUserManual = userTerms && userTerms[userManualTermId || MANUAL_TERM_ID];

  useEffect(() => {
    if (!termUserManual) return;
    if (manualActive) {
      addTerm(userManualTermId || MANUAL_TERM_ID);
    } else {
      removeTerm(userManualTermId || MANUAL_TERM_ID);
    }
  }, [manualActive, termUserManual]);

  useEffect(() => {
    setSideTextStatus(generateSideTextFromAnswers(answers, title));
    setSideTextVisibitlity(false);
    setGuide([[]]);
    setExample([[]]);
    setTerms([]);
  }, [id]);

  useEffect(() => {
    // check if outsideAnswers is different from selected
    const isDifferent =
      outsideAnswers.length !== selected.length ||
      outsideAnswers.filter((e) => !selected.includes(e)).length > 0;
    if (isDifferent) {
      changeStates(generateSelectedAnswerMap(answers, selected));
      setOutsideAnswers(selected);
    }
  }, [answers, outsideAnswers, selected]);

  useEffect(() => {
    if (toReset.value) {
      const answerState: Record<string, any> = {};
      answers.forEach((answer) => {
        const isAlreadySelected =
          selected.filter((selectedAnswer) => selectedAnswer === answer.id)
            .length > 0;
        answerState[answer.id] = isAlreadySelected;
      });
      changeStates(answerState);
      toReset.toChange(false);
    }
  }, [toReset.value]);

  useEffect(() => {
    let isVisible = false;
    Object.keys(sideTextStatus).forEach(
      (key) => (isVisible = isVisible || sideTextStatus[key])
    );
    setSideTextVisibitlity(isVisible);

    if (isVisible && state.tableOfContent) {
      dispatch(setTableOfContent());
    }
  }, [sideTextStatus]);

  useEffect(() => {
    if (terms.length > 0 && state.tableOfContent) {
      dispatch(setTableOfContent());
    }
  }, [terms.length]);

  useEffect(() => {
    if (guides.current) {
      guides.current.style = "translateX(200px)";
    }
  }, [guides.current]);

  const termListId = "term-list";
  const termListRef = useRef<any[]>();

  useEffect(() => {
    const questionEl = document.getElementById(`question-${id}`);

    if (questionEl) {
      questionEl.scrollTo({
        top: questionEl.offsetTop,
        behavior: "smooth",
      });
    }
  }, [id]);

  useEffect(() => {
    const termListDiv = document.getElementById(termListId + id);

    if (terms.length && termListRef.current && termListRef.current.length) {
      const diff = terms.filter((t) => !termListRef.current?.includes(t));
      let newElement = null;
      // @ts-ignore
      if (diff.length !== 0) newElement = document.getElementById(diff[0].id);

      if (termListDiv && diff.length && newElement !== null) {
        termListDiv.scrollTo({
          top: newElement.offsetTop - termListDiv.offsetTop,
          behavior: "smooth",
        });
      }
    }
    termListRef.current = terms;
  }, [terms]);

  useEffect(() => {
    setMultipleIsDifferent((state) => {
      let result = false;
      Object.keys(states).map((k) => {
        if (states[k] && !selected.includes(k)) result = true;
      });
      selected.forEach((a) => {
        if (!states[a]) result = true;
      });
      if (type === "multiple") {
        setCanGoDown(!result);
      }
      return result;
    });
  }, [states]);

  const addTerm = (termId: string) => {
    setTerms((oldTerms) => {
      const isAlreadyPresent = oldTerms.find((t) => t.id === termId) !== undefined;
      const term = userTerms && userTerms[termId];
      if (term && !isAlreadyPresent) {
          return [
          ...oldTerms,
          {
            title: term.name,
            text: JSON.parse(term.description).text,
            id: term.term_id,
          },
        ];
      } else {
        if (state.tableOfContent) {
          dispatch(setTableOfContent());
        }
  
        const termListDiv = document.getElementById(termListId + id);
        const termElement = document.getElementById(termId);
  
        if (termListDiv && termElement) {
          termListDiv.scrollTo({
            top: termElement.offsetTop - termListDiv.offsetTop,
            behavior: "smooth",
          });
        }
        return [...oldTerms];
      }
      }
    );
  };

  function removeTerm(termId: string) {
    const newTerms = terms.filter((t) => t.id !== termId);
    setTerms(newTerms);
  }

  function clickHandle(called: string) {
    if (type === "single") {
      const answerState: Record<string, any> = {};
      answers.forEach(
        (answer) => (answerState[answer.id] = answer.id === called)
      );

      changeStates(answerState);
      onChange(answerState);
    } else if (type === "multiple") {
      const answerState: Record<string, any> = {};
      let isSingle = false;

      answers.forEach((answer) => {
        if (answer.id === called && answer.single) {
          isSingle = true;
          answerState[answer.id] = true;
          changeStates(answerState);
          onChange(answerState);
        }
      });
      if (!isSingle) {
        answers.forEach((answer) => {
          if (answer.id === called) {
            answerState[answer.id] = !states[answer.id];
          } else {
            answerState[answer.id] = answer.single ? false : states[answer.id];
          }
        });
        changeStates(answerState);
      }
    }
  }
  const answers_elements = answers.map((answer) => {
    return (
      <div key={answer.id} className="flex align-top">
        {((answer.guide && answer.guide.length > 0) ||
          (answer.example && answer.example.length > 0)) && (
          <div className="-mr-[1.2rem] mt-2 flex items-center justify-start self-start md:-mr-[3.2rem]">
            <QuestionButton
              isOn={sideTextStatus[answer.id]}
              onClick={() => {
                onQuestionButtonClick(
                  answer.id,
                  answer.guide ? answer.guide : [[]],
                  answer.example ? answer.example : [[]]
                );
              }}
            />
          </div>
        )}
        <div className="ml-8 flex-1 md:ml-16">
          <ModuleQuestion
            label={answer.label}
            id={answer.id}
            selected={states[answer.id]}
            onClick={() => clickHandle(answer.id)}
            type={type}
            single={answer.single}
            setTerms={(id) => {
              addTerm(id);
            }}
          />
        </div>
      </div>
    );
  });

  /* 
     @function onQuestionButtonClick 
     handle help button click
     @param id {string} - id of buttonClicked
     @param guide {string} - guide of button
     @param example {string} - example of button
   */
  const onQuestionButtonClick = (
    id: string,
    guide: any[] | null,
    example: any[] | null
  ) => {
    const newSideText = { ...sideTextStatus };

    Object.keys(sideTextStatus).forEach((v) => {
      if (id === v) {
        newSideText[v] = !sideTextStatus[v];
        if (newSideText[v]) {
          setExample(
            example !== null && example.length === 1 ? example[0] : example
          );
          setGuide(guide !== null && guide.length === 1 ? guide[0] : guide);
        }
      } else {
        newSideText[v] = false;
      }
    });

    setSideTextStatus({ ...newSideText });
  };

  return (
    <div id={`question-${id}`}>
      {sideTextVisibility && !state.isHalfPage && (
        <HoverPage
          onClose={() => {
            let currentSideText = "";
            Object.keys(sideTextStatus).forEach((key) => {
              if (sideTextStatus[key]) {
                currentSideText = key;
              }
            });
            onQuestionButtonClick(currentSideText, null, null);
          }}
        >
          <div className="pt-7">
            <GuideExample guide={guide} example={example} addTerm={addTerm} />
          </div>
        </HoverPage>
      )}
      {sideTextVisibility && (
        <motion.div
          className={"absolute left-6 top-36 w-[22vw] pt-5"}
          initial={{ x: "-100vw" }}
          animate={{ x: 10 }}
          transition={{ type: "spring", damping: 30 }}
        >
          <GuideExample
            guide={guide}
            example={example}
            addTerm={addTerm}
            visible={!state.tableOfContent}
          />
        </motion.div>
      )}
      {terms.length > 0 && (
        <Terms
          isHalfPage={state.isHalfPage}
          termListId={termListId + id}
          onRemoveAll={() => {
            setTerms([]);
            dispatch(deactivateManualForQuestion(id));
          }}
        >
          {terms.map((t, i) => {
            return (
              <Term
                title={t.title}
                text={t.text}
                key={i}
                index={i}
                termId={t.id}
                onRemove={(index) => {
                  if (t.id === MANUAL_TERM_ID || t.id === userManualTermId) {
                    dispatch(deactivateManualForQuestion(id));
                  }
                  setTerms((oldT) => {
                    oldT.splice(index, 1);
                    return [...oldT];
                  });
                }}
                setTerms={(id) => {
                  addTerm(id);
                }}
              />
            );
          })}
        </Terms>
      )}
      <motion.div
        id={id}
        className={`lg:pt-46 relative h-screen w-11/12 max-w-[450px] pt-36 transition sm:w-4/5 md:w-[700px] md:max-w-none lg:w-1/2 lg:pl-5 lg:pr-10 ${
          state.isHalfPage && state.tableOfContent ? "ml-14" : "m-auto"
        }`}
      >
        <div className="relative  flex h-5/6 flex-col items-start lg:h-[85%]">
          <div
            className="h-full w-full overflow-y-auto"
            id={`question-body-${id}`}
          >
            <div className="flex">
              {(title.guide.length > 0 || title.example.length > 0) && (
                <div className="-mr-[3.1rem] mt-4 flex self-start">
                  <QuestionButton
                    isOn={sideTextStatus["title"]}
                    onClick={() =>
                      onQuestionButtonClick(
                        "title",
                        title.guide ? title.guide : [[]],
                        title.example ? title.example : [[]]
                      )
                    }
                  />
                </div>
              )}
              <div className="ml-[4.4rem] flex-1 leading-7">
                <RichTextReader
                  text={title.text.text}
                  size={"1.5em"}
                  setTerms={(id) => {
                    addTerm(id);
                  }}
                />
                {conclusion && print && (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon
                      icon={faInfo}
                      style={{ background: color2 }}
                      color="white"
                      className="h-2 w-2 rounded-full p-1 text-black"
                    />
                    This conclusion will be printed in your Final Report.
                  </div>
                )}
              </div>
            </div>

            <div className="flex w-full flex-col gap-2">
              <span className="ms-[5rem]">
                {type === "multiple" ? (
                   <div className="flex items-center gap-2">
                   <FontAwesomeIcon
                     icon={faInfo}
                     style={{ background: color2 }}
                     color="white"
                     className="h-2 w-2 rounded-full p-1 text-black"
                   />
                     Choose all that apply.
                 </div>
                ) : (
                  ""
                )}
              </span>
              {answers_elements}
              {((type === "multiple" &&
                multipleIsDifferent &&
                Object.keys(states).filter((k) => states[k]).length !== 0) ||
                (conclusion && !iterated)) && (
                <div className="flex w-full">
                  <Button
                    label="continue"
                    className="mx-auto my-4"
                    onClick={() => {
                      onChange(states);
                      setCanGoDown(true);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/*
  @function generateSideTextFromAnswers
  generate sideText help imporfation from answers
  @params answers {array} - given answers
  @params title  {object} - guide and example for title
  @returns {object} - sideText object
 */
function generateSideTextFromAnswers(answers: any[], title: any) {
  const result: Record<string, any> = {};
  answers.forEach((answer: any) => (result[answer.id] = false));
  if ("guide" in title && "example" in title) result["title"] = false;

  return result;
}

function generateSelectedAnswerMap(
  answers: Array<{
    id: string;
    label: string;
  }>,
  selected: any
) {
  const answerState: Record<string, any> = {};
  answers.forEach((answer) => {
    const isAlreadySelected =
      selected.filter((selectedAnswer: any) => selectedAnswer === answer.id)
        .length > 0;
    answerState[answer.id] = isAlreadySelected;
  });

  return answerState;
}
